/**
 * Shared plumbing for the cloud data layer:
 *   - current-user resolution from the live Supabase auth session,
 *   - a uniform error wrapper (callers handle the thrown errors in a later chunk),
 *   - a factory for the five "append-style" tables that all share the same
 *     fetchAll / upsert / upsertMany / remove / removeAll surface.
 */
import { supabase } from '../supabaseClient';

/** Throw a clear, contextualised error when a Supabase call returns one. */
export function assertNoError(
  error: { message: string } | null | undefined,
  context: string
): void {
  if (error) {
    throw new Error(`Cloud data error during ${context}: ${error.message}`);
  }
}

/**
 * Resolve the current user's id from the live Supabase session. Throws when no
 * user is authenticated — every cloud table is scoped to `user_id = auth.uid()`,
 * so there is nothing to read or write without a session.
 */
export async function getCurrentUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  assertNoError(error, 'getCurrentUserId');
  const id = data.user?.id;
  if (!id) {
    throw new Error(
      'Cloud data error: no authenticated user in the current Supabase session.'
    );
  }
  return id;
}

/** The CRUD surface shared by every append-style (per-row) table. */
export interface AppendTable<App, Row> {
  /** App object -> DB row. `userId` populates the owning `user_id` column. */
  toRow: (app: App, userId: string) => Row;
  /** DB row -> app object. */
  fromRow: (row: any) => App;
  fetchAll: (userId: string) => Promise<App[]>;
  upsert: (row: Row) => Promise<void>;
  upsertMany: (rows: Row[]) => Promise<void>;
  remove: (id: string) => Promise<void>;
  removeAll: (userId: string) => Promise<void>;
  /**
   * Delete the user's rows whose `id` is NOT in `keepIds` — the "prune stale
   * rows" half of a reconcile (upsert current rows first, then prune). When
   * `keepIds` is empty this deletes ALL the user's rows (local array is empty).
   */
  removeNotIn: (userId: string, keepIds: string[]) => Promise<void>;
}

/**
 * Build the standard CRUD surface for an append-style table whose primary key
 * is `id` and which carries a `user_id` owner column. The caller supplies the
 * two serializers; everything else is generic.
 */
export function makeAppendTable<App, Row>(
  table: string,
  toRow: (app: App, userId: string) => Row,
  fromRow: (row: any) => App
): AppendTable<App, Row> {
  return {
    toRow,
    fromRow,
    async fetchAll(userId: string): Promise<App[]> {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('user_id', userId);
      assertNoError(error, `fetchAll(${table})`);
      return (data ?? []).map(fromRow);
    },
    async upsert(row: Row): Promise<void> {
      const { error } = await supabase.from(table).upsert(row as any);
      assertNoError(error, `upsert(${table})`);
    },
    async upsertMany(rows: Row[]): Promise<void> {
      if (rows.length === 0) return;
      const { error } = await supabase.from(table).upsert(rows as any);
      assertNoError(error, `upsertMany(${table})`);
    },
    async remove(id: string): Promise<void> {
      const { error } = await supabase.from(table).delete().eq('id', id);
      assertNoError(error, `remove(${table})`);
    },
    async removeAll(userId: string): Promise<void> {
      const { error } = await supabase.from(table).delete().eq('user_id', userId);
      assertNoError(error, `removeAll(${table})`);
    },
    async removeNotIn(userId: string, keepIds: string[]): Promise<void> {
      let q = supabase.from(table).delete().eq('user_id', userId);
      if (keepIds.length > 0) {
        const list = keepIds.map((id) => `"${id}"`).join(',');
        q = q.not('id', 'in', `(${list})`);
      }
      const { error } = await q;
      assertNoError(error, `removeNotIn(${table})`);
    },
  };
}
